/**
 * Email validation utilities
 * 
 * Provides domain blocklist checking for free email providers
 * and disposable email services. Used for work-email-only validation.
 */

import { HUBSPOT_BLOCKED_DOMAINS } from "@/utils/hubspotBlockedDomains";

/**
 * Normalize email address (lowercase, trim)
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Extract domain from email address
 */
export function extractDomain(email: string): string | null {
  const normalized = normalizeEmail(email);
  const parts = normalized.split("@");
  if (parts.length !== 2) {
    return null;
  }
  return parts[1] || null;
}

/**
 * Check if email domain is in the free/disposable blocklist
 */
export function isBlockedDomain(email: string): boolean {
  const domain = extractDomain(email);
  if (!domain) {
    return false;
  }
  return HUBSPOT_BLOCKED_DOMAINS.has(domain);
}

/**
 * Validate email format
 */
export function isValidEmailFormat(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Comprehensive email validation for work emails only
 * 
 * @param email - Email address to validate
 * @returns Object with validation result and error message if invalid
 */
export function validateWorkEmail(email: string): {
  valid: boolean;
  error?: string;
} {
  // Check format
  if (!email || !isValidEmailFormat(email)) {
    return {
      valid: false,
      error: "Please enter a valid email address.",
    };
  }

  // Check blocklist
  if (isBlockedDomain(email)) {
    const domain = extractDomain(email);
    return {
      valid: false,
      error: `This email domain (${domain}) is not accepted. Please use a business or professional email address instead of a free email provider.`,
    };
  }

  return { valid: true };
}
